// Repair Order Types and Enums

export const RepairItemTypes = [
  'Chassis',
  'Container',
  'Van',
  'Truck',
  'Mule',
  'Golf Cart',
  'TOPLOADER',
  'Other',
] as const;

export type RepairItemType = typeof RepairItemTypes[number];

export enum RepairOrderStatus {
  Open = 'Open',
  Dispatched = 'Dispatched',
  Pending = 'Pending',
  Deferred = 'Deferred',
  Completed = 'Completed',
  Closed = 'Closed',
  Cancelled = 'Cancelled',
  Draft = 'Draft',
}

export const RepairOrderStatusColors: Record<string, string> = {
  Draft: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  Open: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  Dispatched: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  Pending: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  Completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  Closed: 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
  Cancelled: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  Deferred: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
};

export enum RepairLocationType {
  GWIFacility = 'GWI Yard / Warehouse / Facility',
  Customer = 'Customer',
  Roadside = 'Roadside',
}

/**
 * Parties a repair-order cost can be charged back to. Drives the first of the
 * two "Billed To" fields; the second picks an entity from the matching lookup
 * (Driver -> getDrivers, Customer -> getCustomerLocations, Terminal -> getGWILocations).
 */
export const BilledToTypes = ['Driver', 'Customer', 'Terminal'] as const;
export type BilledToType = typeof BilledToTypes[number];

export enum AxleType {
  Front = 'Front',
  Middle = 'Middle',
  Rear = 'Rear',
}

export enum PositionType {
  Inner = 'Inner',
  Outer = 'Outer',
}

export enum SideType {
  Left = 'Left',
  Right = 'Right',
}

/** What a maintenance ping is asking the maintenance team for. */
export type RepairOrderPingType = 'eta' | 'completion';

/**
 * An outstanding "ping" asking maintenance for an ETA or a completion status
 * on a repair order (POST /repair-orders/{recno}/pings).
 */
export interface RepairOrderPing {
  recno: number;
  /** 'eta' = when will the vendor arrive; 'completion' = is the repair done. */
  requestType: RepairOrderPingType;
  /** Short-form username of the requester, e.g. 'CCORCORAN'. */
  requestedBy: string;
  /**
   * When the ping was made, as a NAIVE LOCAL-TIME ISO 8601 string with NO 'Z'
   * and NO offset — e.g. '2026-09-22T08:30:00'. MariaDB DATETIME is
   * timezone-less and the API .isoformat()s it as-is.
   *
   * `new Date(requestedAt)` parses an offset-less date-time as LOCAL time,
   * which is exactly right here. DO NOT "normalize" it by appending 'Z' and
   * DO NOT treat it as UTC: doing so shifts relative-age math by the
   * America/Chicago offset and a ping made a minute ago renders as
   * "5 hours ago".
   *
   * null when the DB value is missing / a V1 zero date.
   */
  requestedAt: string | null;
}

/**
 * A ping from the GLOBAL feed (GET /repair-orders/pings/count), which carries
 * the order identity too — the sidebar raises toasts for orders the current
 * page has never heard of, so the ping has to name itself.
 */
export interface OpenPingFeedItem extends RepairOrderPing {
  /** The repair order the request is about. Always present. */
  roRecno: number;
  /**
   * The PRO the repair order hangs off. NULLABLE: `pronumber` is a
   * denormalized column on the repair order with no FK behind it, so an order
   * can genuinely have none. Name the order by `roRecno` when this is null —
   * never render "PRO null".
   */
  pronumber: number | null;
}

/**
 * GET /repair-orders/pings/count.
 *
 * `open` and `pings` are allowed to DISAGREE and that divergence is deliberate:
 * `open` is the true global total (what the badge must show), while `pings` is
 * only the newest 50, newest first. Never derive the count from
 * `pings.length` — past the cap it under-reports.
 */
export interface OpenPingFeed {
  open: number;
  pings: OpenPingFeedItem[];
}

/** Small repair-order card for the Pro Details ROs tab (GET /repair-orders/by-pro/{pro}). */
export interface RepairOrderCard {
  recno: number;
  orderStatus: string;
  reportedByName: string;
  repairPart: string;
  category: string;
  issue: string;
  /** Latest of created / dispatched / completed, MM/DD/YYYY (or null if none). */
  latestDate: string | null;
  /** Which timestamp latestDate came from: 'Created' | 'Dispatched' | 'Completed'. */
  latestDateLabel: string | null;
  /** Assigned vendor's name; '' (empty string, never null) when no vendor is assigned. */
  vendorName: string;
  /** Vendor's committed ETA, formatted 'MM/DD/YYYY HH:MM'; null when unset or a V1 zero date. */
  vendorEta: string | null;
  /**
   * ETA MINUTES from dispatch — a number-as-string, e.g. '90', NOT a time and
   * NOT free text. It is the "ETA Minutes" input on the repair-order drawer's
   * dispatch section, which auto-computes `vendoretadatetime` (the read-only
   * "ETA to Repair Location") from dispatch time + these minutes.
   *
   * So do NOT render it as if it were a clock time: '90' means 90 minutes.
   * Prefer `vendorEta`, which is already an absolute formatted datetime.
   * '' when none.
   *
   * In practice the two move together — the drawer requires both once a vendor
   * and dispatch time are set, and 0 of 1,891 prod dispatches since Aug 2026
   * had this without `vendorEta` — so a `repairEta`-only card is not a state
   * the UI currently reaches.
   */
  repairEta: string;
  /** Outstanding maintenance pings, newest first; [] when none. */
  openPings: RepairOrderPing[];
}

export interface RepairOrderListItem {
  recno: number;
  v2repairorderid: number | null;
  orderstatus: string | null;
  reportedbytype: string | null;
  reportedbyname: string | null;
  pronumber: number | null;
  container: string | null;
  chassisid: string | null;
  repairpart: string | null;
  locationtype: string | null;
  locationdesc: string | null;
  createdatetime: string | null;
  lastchangedatetime: string | null;
  primary_issue: string | null;
  vendor_assigned: boolean;
  vendor_name: string | null;
  waiting: string | null;
  perdiem_date: string | null;
  cutoff_date: string | null; // MM/DD/YYYY HH:MM
  appointment_date: string | null; // MM/DD/YYYY [HH:MM [- HH:MM]]
  // Export-only fields
  withequipment: string | null;
  repairtype: string | null;
  category_name: string | null;
  repairqty: number | null;
  tire_size: string | null;
  customerlocationname: string | null;
  location_name: string | null;
  breakdownaddress: string | null;
  chassispool: string | null;
  region: string | null;
  estimateddispatchdatetime: string | null;
  repairdispatchdatetime: string | null;
  // Export-only: dispatch/complete datetimes with the time kept (on-screen
  // columns use the date-only fields).
  repairdispatchdatetime_full?: string | null;
  repaireta: string | null;
  vendoretadatetime: string | null;
  dispatchedby: string | null;
  repaircomments: string | null;
  drivercomments: string | null;
  repaircompletedatetime: string | null;
  repaircompletedatetime_full?: string | null;
  // Derived server-side (not cache columns): the fleet unit the order belongs
  // to (chassisid, or container for Truck/Van/Mule/... orders the FastCMS
  // drawer stored there; null for container-only orders) and its lifetime RO
  // count, who completed the order + most recent note. See
  // list_repair_orders enrichment.
  equipment_unit: string | null;
  equipment_ro_count: number | null;
  /** The displayed equipment has active Customer Locations in Equipment List. */
  equipment_has_customer_locations?: boolean;
  completedby: string | null;
  last_note: string | null;
  last_note_date: string | null;
  last_note_by: string | null;
  /**
   * Outstanding maintenance requests on this order, from the same batched
   * lookup the PRO drawer's cards use. Always an array — `[]` when there are
   * none, never null. See the list_repair_orders enrichment.
   */
  open_pings: RepairOrderPing[];
  estimatedcost: number | null;
  invoicenumber: string | null;
  invoicetotal: number | null;
  // Bill-to attribution. Derived server-side (not cache columns) — see the
  // list_repair_orders enrichment.
  billedtotype: string | null;
  billedtoname: string | null;
  reschedule_count: number;
  reschedule_history: { reason: string; old_date: string; new_date: string; created: string }[];
  attachment_count: number;
  attachment_details: { documenttype: string; uploadedby: string; uploaded: string }[];
}

export interface TireLoc {
  recno?: number;
  side: string;
  axle: string;
  position: string;
}

export interface RepairCategory {
  recno: number;
  name: string;
  repairtype: string;
  istire: string;
}

export interface RepairDispatch {
  recno?: number;
  repairrequestrecno?: number;
  dispatchstatus?: string;
  vendorrecno?: number | null;
  vendor_name?: string;
  dispatchedby?: string;
  estimateddispatchdatetime?: string | null;
  repairdispatchdatetime?: string | null;
  repaireta?: string;
  vendoretadatetime?: string | null;
  repairarrivedatetime?: string | null;
  repairdepartdatetime?: string | null;
  repairemail?: string;
  drivercomments?: string;
}

export interface VendorLookup {
  recno: number;
  name: string;
  vendoremail?: string;
}

export interface GWILocation {
  recno: number;
  name: string;
  address1: string | null;
  address2: string | null;
  city: string | null;
  county: string | null;
  state: string | null;
  zip: string | null;
  latitude: number | null;
  longitude: number | null;
}

export interface RepairRequest {
  recno?: number;
  repairtype: string;
  repairstatus?: string;
  repaircategoryrecno?: number;
  repaircategory_name?: string;
  repaircategory_istire?: string;
  repairissue: number;
  repairissue_name?: string;
  repairqty?: number;
  repairtiresize?: string;
  repaircomments?: string;
  tirelocs?: TireLoc[];
  dispatch?: RepairDispatch | null;
}

export interface RepairOrderDetail {
  recno: number;
  v2repairorderid: number | null;
  orderstatus: string | null;
  reportedbytype: string | null;
  reportedbyname: string | null;
  reportedbycontactnum: string | null;
  driverrecno: number | null;
  pronumber: number | null;
  container: string | null;
  chassisid: string | null;
  chassispool: string | null;
  repairpart: string | null;
  withequipment: string | null;
  waiting: string | null;
  locationtype: string | null;
  locationdesc: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  county: string | null;
  state: string | null;
  zip: string | null;
  locationid: number | null;
  customerlocationname: string | null;
  longitude: number | null;
  latitude: number | null;
  region: string | null;
  estimatedcost: number | null;
  invoicenumber: string | null;
  invoicetotal: number | null;
  billedtotype: string | null;
  billedtorecno: number | null;
  billedtoname: string | null;
  createdby: string | null;
  createdatetime: string | null;
  lastchangedby: string | null;
  lastchangedatetime: string | null;
  requests: RepairRequest[];
}

export interface RepairIssue {
  recno: number;
  repairtype: string;
  issue: string;
  repaircategoryrecno?: number;
}

export interface TireSize {
  recno: number;
  size: string;
}

export interface ChassisPool {
  recno: number;
  name: string;
  abbreviation: string;
}

export interface DriverLookup {
  recno: number;
  unitnumber: string;
  firstname: string;
  lastname: string;
  phone: string;
  region: string;
  active: boolean;
}

export interface EmployeeLookup {
  display_name: string;
  email: string;
  v1_username: string;
}

export const DocumentTypes = ['Invoice', 'Photo', 'Work Order', 'Other'] as const;
export type DocumentType = (typeof DocumentTypes)[number];

export interface RepairOrderDocument {
  map_recno: number;
  fileinfo_recno: number;
  documenttype: string;
  description: string | null;
  filename: string;
  filetype: string;
  directory: string;
  uploaddatetime: string | null;
  uploadedby: string | null;
  url: string;
}
